import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useSelector } from "react-redux";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import BookingQrCode from "../Common/BookingQrCode";
// import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { getAPI, postAPI } from "../api/callAPI";
import moment from "moment";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

const BookingDetails = () => {
    useDocumentTitle("Booking Details");
    const { bookingID } = useParams();
    const { endpoint, store } = useSelector((state) => state.auth);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [details, setDetails] = useState(null);
    const [storeName, setStoreName] = useState("");

    const bookingForQr = useMemo(() => {
        if (!details) return null;
        const dtRaw =
            details.booking_datetime ||
            details.date_time ||
            details.datetime ||
            details.bookingDateTime;
        return {
            id: details.id || bookingID,
            booking_id: details.booking_id,
            booking_datetime: dtRaw || "",
            store: {
                id: details.store?.id || details.store_id || details.storeId,
            },
            customer_id: details.customer_id,
        };
    }, [details, bookingID]);

    useEffect(() => {
        const fetchDetails = async () => {
            if (!bookingID) {
                setError("Invalid booking ID");
                setLoading(false);
                return;
            }
            setLoading(true);
            setError("");
            try {
                // Try a GET first; if backend expects POST, fall back
                // Common patterns observed in project: postAPI to endpoints like `online_bookings`
                // We'll try sensible endpoints
                let resp = null;
                try {
                    resp = await getAPI(endpoint, `online_bookings/${bookingID}`);
                } catch (e) {
                    // fallback to POST patterns
                    try {
                        resp = await postAPI(endpoint, "admin/booking-detail", { id: bookingID });
                    } catch (e2) {
                        // final fallback: search endpoint by id
                        // resp = await postAPI(endpoint, "admin/search-booking", { text_search: String(bookingID) });
                    }
                }

                const data = resp?.data ?? resp;
                let d = null;
                if (Array.isArray(data)) {
                    d = data[0] || null;
                } else if (data && typeof data === "object") {
                    d = data;
                }
                if (!d) throw new Error("Booking not found");
                setDetails(d);
                // fetch store name if available
                const theStoreId = d?.store_id || d?.storeId;
                if (theStoreId) {
                    try {
                        const s = await getAPI(endpoint, `stores/${theStoreId}`);
                        const sData = s?.data?.["0"] || s?.data || s;
                        if (sData && sData.name) setStoreName(sData.name);
                    } catch (_) {
                        // ignore if store fetch fails
                    }
                }
            } catch (err) {
                setError(err?.message || "Failed to load booking details");
            } finally {
                setLoading(false);
            }
        };
        fetchDetails();
    }, [bookingID, endpoint]);

    const ui = useMemo(() => {
        const men = Number(details?.num_of_men || 0) || 0;
        const women = Number(details?.num_of_women || 0) || 0;
        const adult = Number(details?.adult || details?.guests || 0) || 0;
        const child = Number(details?.child || 0) || 0;
        const kids = Number(details?.infants || 0) || 0;
        const hasGenderSplit = Boolean(men || women);
        const adultGuests = hasGenderSplit ? men + women : adult;
        const guests = adultGuests + child + kids;
        const bookingId = details?.id || bookingID || "";
        const firstName = details?.first_name || details?.firstName || "";
        const lastName = details?.last_name || details?.lastName || "";
        const phone = details?.phone || details?.mobile || details?.mobile_no || "";
        const email = details?.email || details?.email_id || "";
        const seatingPreference = details?.seating_preference || details?.seating || details?.preference || "";
        // Try to normalize booking datetime fields
        const dtRaw = details?.booking_datetime || details?.date_time || details?.datetime || details?.bookingDateTime;
        const dateTime = dtRaw ? moment(dtRaw).format("DD-MM-YYYY, hh:mm a") : "";
        const entryFee = Number(details?.entry_fee || 0) || 0;
        const coverCharge = Number(details?.cover_charge || 0) || 0;
        const currencySymbol = store?.currency_symbol || "";
        const isCoverBooking = entryFee > 0 || coverCharge > 0 || Boolean(details?.is_cover_booking);
        const fullName = [firstName, lastName].filter(Boolean).join(" ");
        const totalAmount = entryFee * adultGuests + coverCharge;
        
        // Check if QR code should be shown
        let shouldShowQR = true;
        if (dtRaw) {
            const bookingMoment = moment(dtRaw);
            const currentMoment = moment();
            
            // Show QR if booking hasn't passed yet
            if (currentMoment.isBefore(bookingMoment)) {
                shouldShowQR = true;
            } else {
                // Show QR if booking has passed but within 4 hours
                const hoursDiff = currentMoment.diff(bookingMoment, 'hours', true);
                shouldShowQR = hoursDiff <= 4;
            }
        }
        
        return {
            guests,
            men,
            women,
            adultGuests,
            child,
            kids,
            bookingId,
            fullName,
            phone,
            email,
            entryFee,
            coverCharge,
            isCoverBooking,
            totalAmount,
            currencySymbol,
            seatingPreference,
            dateTime,
            shouldShowQR,
        };
    }, [details, bookingID, store?.currency_symbol]);

    if (loading) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: "center" }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Paper elevation={2} sx={{ p: 3 }}>
                    <Typography variant="h6">Unable to load booking</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {error}
                    </Typography>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
                {/* <CheckCircleOutlineIcon sx={{ fontSize: 80, color: "green", mb: 2 }} /> */}
                <Typography variant="h5" gutterBottom fontWeight="bold">
                    Booking Details
                </Typography>
                {storeName ? (
                    <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                        <strong>{storeName}</strong>
                    </Typography>
                ) : null}
                {/* <Typography variant="body1" sx={{ mt: 2 }}>
          Reservation for <strong>{ui.guests}</strong> guests.
        </Typography> */}

                {/* {ui.isCoverBooking && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            To confirm booking, please pay on the gate. Ask staff for any assistance.
          </Typography>
        )} */}
                {ui.shouldShowQR ? (
                    <>
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                            <Box sx={{ borderRadius: 1, overflow: "hidden" }}>
                                <BookingQrCode
                                    booking={bookingForQr}
                                    customerId={details?.customer_id}
                                    appConfigName={endpoint}
                                    size={200}
                                    style={{ width: "200px" }}
                                />
                            </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Booking ID: <strong>#{ui.bookingId}</strong>
                        </Typography>
                    </>
                ) : (
                    <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            QR Code is no longer available.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Booking ID: <strong>#{ui.bookingId}</strong>
                        </Typography>
                    </Box>
                )}

                <Divider sx={{ my: 1.5 }} />

                {ui.dateTime ? (
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                        <Typography variant="body2">Date & Time</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>{ui.dateTime}</strong>
                        </Typography>
                    </Box>
                ) : null}

                {!!ui.guests && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                        <Typography variant="body2">Guests</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>{ui.guests}</strong>
                        </Typography>
                    </Box>
                )}

                {!!ui.adultGuests && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                        <Typography variant="body2">- Adults</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>{ui.adultGuests}</strong>
                        </Typography>
                    </Box>
                )}
                {!!ui.men && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                        <Typography variant="body2">- Men</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>{ui.men}</strong>
                        </Typography>
                    </Box>
                )}
                {!!ui.women && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                        <Typography variant="body2">- Women</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>{ui.women}</strong>
                        </Typography>
                    </Box>
                )}
                {!!ui.child && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                        <Typography variant="body2">- Child</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>{ui.child}</strong>
                        </Typography>
                    </Box>
                )}
                {!!ui.kids && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                        <Typography variant="body2">- Kids</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>{ui.kids}</strong>
                        </Typography>
                    </Box>
                )}
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                    <Typography variant="body2">Name</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>{ui.fullName || "-"}</strong>
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                    <Typography variant="body2">Phone</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>{ui.phone || "-"}</strong>
                    </Typography>
                </Box>
                {ui.email ? (
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                        <Typography variant="body2">Email</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>{ui.email}</strong>
                        </Typography>
                    </Box>
                ) : null}
                {details?.city ? (
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                        <Typography variant="body2">City</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>{String(details.city)}</strong>
                        </Typography>
                    </Box>
                ) : null}
                <Divider sx={{ my: 1.5 }} />



                {ui.seatingPreference ? (
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                        <Typography variant="body2">Seating Preference</Typography>
                        <Typography variant="body2">{ui.seatingPreference}</Typography>
                    </Box>
                ) : null}

                {ui.isCoverBooking && (
                    <Box sx={{ mt: 3, textAlign: "left" }}>
                        {/* <Typography variant="subtitle2" sx={{ mb: 1 }}>Cover Booking</Typography> */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                            <Typography variant="body2">Entry Fee (Per Person)</Typography>
                            <Typography variant="body2">{`${ui.currencySymbol}${ui.entryFee * ui.adultGuests}`}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">{`(${ui.adultGuests} Guests × ${ui.currencySymbol}${ui.entryFee} per person = ${ui.currencySymbol}${ui.entryFee * ui.adultGuests})`}</Typography>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                            <Typography variant="body2">Cover Charge</Typography>
                            <Typography variant="body2">{`${ui.currencySymbol}${ui.coverCharge}`}</Typography>
                        </Box>
                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="subtitle1" fontWeight="bold">Total Amount</Typography>
                            <Typography variant="subtitle1" fontWeight="bold">{`${ui.currencySymbol}${ui.totalAmount}`}</Typography>
                        </Box>
                    </Box>
                )}

                <Divider sx={{ my: 1.5 }} />

                {/* Additional meta */}
                <Box sx={{ mt: 3, textAlign: "left" }}>
                    {details?.payment_mode ? (
                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                            <Typography variant="body2">Payment Mode</Typography>
                            <Typography variant="body2">{String(details.payment_mode)}</Typography>
                        </Box>
                    ) : null}
                    {details?.payment_status ? (
                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                            <Typography variant="body2">Payment Status</Typography>
                            <Typography variant="body2">{String(details.payment_status)}</Typography>
                        </Box>
                    ) : null}
                </Box>
            </Paper>
        </Container>
    );
};

export default BookingDetails;


